/**
 * Table Body Component
 *
 * Renders the standard (non-virtualized) table body.
 */
import { Fragment, useEffect, useRef } from "react";
import { getRowRenderVersion } from "../utils";
import { DataRow, MemoizedDataRow } from "./DataRow";
import { EmptyState, LoadingSkeleton } from "./TableStates";
import type { TableBodyProps } from "../types";
import type { RowData } from "@tanstack/react-table";

export const TableBody = <T extends RowData>({
  hasNoVisibleColumns,
  columns,
  isLoading,
  data,
  isLoadingMore,
  skeletonRowCount,
  visibleColumns,
  table,
  onRowClick,
  getRowClassName,
  isHighlightedRow,
  highlightedRowId,
  tooltipClassName,
  enableColumnPinning,
  fetchNextPage,
  hasNextPage,
  renderExpandedRow,
  expandedRowIds,
  getRowId,
  getRowProps,
  memoizeRows = false,
}: TableBodyProps<T>) => {
  const loadMoreTriggerRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || isLoading || isLoadingMore) {
      return;
    }

    const trigger = loadMoreTriggerRef.current;
    if (!trigger) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          fetchNextPage().catch(() => {});
        }
      },
      { root: null, rootMargin: "200px 0px 200px 0px", threshold: 0 },
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isLoading, isLoadingMore, data.length]);

  const Row = memoizeRows ? MemoizedDataRow : DataRow;
  const renderVersion = memoizeRows ? getRowRenderVersion(table) : undefined;

  return (
    <tbody className="bg-white divide-y divide-gray-200">
      {hasNoVisibleColumns && !isLoading
        ? (
          <EmptyState colSpan={columns.length} />
        )
        : !hasNoVisibleColumns && isLoading && (data.length === 0 || !isLoadingMore)
          ? (
            <LoadingSkeleton colSpan={visibleColumns.length} rowCount={skeletonRowCount} />
          )
          : (
            !hasNoVisibleColumns &&
            table.getRowModel().rows.map((row) => {
              const rowId = getRowId?.(row.original);
              const isExpanded = rowId !== undefined && rowId !== null && expandedRowIds?.includes(rowId);
              return (
                <Fragment key={row.id}>
                  <Row
                    row={row}
                    onRowClick={onRowClick}
                    getRowClassName={getRowClassName}
                    isHighlightedRow={isHighlightedRow}
                    highlightedRowId={highlightedRowId}
                    tooltipClassName={tooltipClassName}
                    enableColumnPinning={enableColumnPinning}
                    getRowProps={getRowProps}
                    renderVersion={renderVersion}
                  />
                  {isExpanded && renderExpandedRow && (
                    <tr>
                      <td colSpan={visibleColumns.length} className="p-0">
                        {renderExpandedRow(row.original)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
      {isLoadingMore &&
        Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
          <tr key={`loading-more-${rowIndex}`} className="animate-pulse">
            <td colSpan={visibleColumns.length} className="px-6 py-4">
              <div className="h-4 bg-gray-300 rounded w-full" />
            </td>
          </tr>
        ))}
      {hasNextPage && !isLoadingMore && (
        <tr ref={loadMoreTriggerRef} aria-hidden="true">
          <td colSpan={visibleColumns.length} className="h-1 p-0" />
        </tr>
      )}
    </tbody>
  );
};
